import { useState } from 'react';
import {
  CONTACT_SOORTEN,
  UITKOMSTEN_BEZOEK,
  type Contactmoment,
  type ContactSoort,
  type UitkomstBezoek,
} from '@/types/domain';
import { nieuweUuid } from '@/utils/id';
import { datumNL, vandaagISO } from '@/utils/format';
import { Tekstveld, Tekstvak } from '@/components/ui/Velden';
import { Schrijftip } from './Schrijftip';
import { Knop } from '@/components/ui/Knop';
import {
  BriefIcoon,
  DeurIcoon,
  EnvelopIcoon,
  GemeenteIcoon,
  PotloodIcoon,
  PrullenbakIcoon,
  TelefoonIcoon,
} from '@/components/layout/Iconen';
import { TegelKiezer } from './TegelKiezer';
import './form.css';

/** Icoon per soort contact; alleen waar het onderscheid maakt. */
const CONTACT_ICONEN: Partial<Record<ContactSoort, JSX.Element>> = {
  'Bezoek aan de deur': <DeurIcoon />,
  'Telefonisch contact': <TelefoonIcoon />,
  'E-mail van de bewoner': <EnvelopIcoon />,
  'Brief of folder bezorgd': <BriefIcoon />,
  'Informatie via de gemeente': <GemeenteIcoon />,
};

interface ContactmomentenProps {
  momenten: Contactmoment[];
  /** Naam van de ingelogde coach; voorvulling bij een nieuw moment. */
  coach: string;
  /**
   * Klap het eerste contactmoment meteen open.
   *
   * Bij een woning die nog nooit is geregistreerd is dat bezoek precies wat
   * de coach komt invullen; die eerst op een potloodje laten zoeken is een
   * stap zonder doel. Bij een bestaand adres blijft alles dicht: dan kom je
   * meestal voor iets anders.
   */
  openEerste?: boolean;
  bijWijziging: (bijwerken: (huidig: Contactmoment[]) => Contactmoment[]) => void;
}

/**
 * De contactmomenten bij één adres.
 *
 * Een nieuw moment toevoegen is een aparte, zichtbare handeling. Dat is met
 * opzet: opslaan na een correctie mag geen bezoek opleveren dat er nooit is
 * geweest. Wie iets bijwerkt zonder dat er contact was, laat deze lijst met
 * rust.
 */
export function Contactmomenten({
  momenten,
  coach,
  openEerste = false,
  bijWijziging,
}: ContactmomentenProps): JSX.Element {
  // Alleen bij het opbouwen: daarna bepaalt de coach zelf wat open staat.
  const [bewerktId, setBewerktId] = useState<string | null>(
    openEerste ? (momenten[0]?.id ?? null) : null
  );

  const nieuwMoment = () => {
    const moment: Contactmoment = {
      id: nieuweUuid(),
      datum: vandaagISO(),
      soort: 'Bezoek aan de deur',
      coach,
      uitkomst: '',
      toelichting: '',
    };
    bijWijziging((huidig) => [...huidig, moment]);
    setBewerktId(moment.id);
  };

  const zet = <K extends keyof Contactmoment>(id: string, veld: K, waarde: Contactmoment[K]) => {
    bijWijziging((huidig) =>
      huidig.map((moment) => (moment.id === id ? { ...moment, [veld]: waarde } : moment))
    );
  };

  const verwijder = (id: string) => {
    bijWijziging((huidig) => huidig.filter((moment) => moment.id !== id));
    setBewerktId(null);
  };

  return (
    <>
      <ol className="contactlijst">
        {momenten.map((moment, index) => {
          const open = bewerktId === moment.id;
          const isEerste = index === 0;

          return (
            <li key={moment.id} className={`contact${open ? ' contact--open' : ''}`}>
              <div className="contact__kop">
                <span className="contact__nummer">{index + 1}</span>
                <div className="contact__samenvatting">
                  <span className="contact__soort">{moment.soort}</span>
                  <span className="contact__meta">
                    {datumNL(moment.datum) || 'geen datum'}
                    {moment.uitkomst ? ` · ${moment.uitkomst}` : ''}
                    {moment.coach ? ` · ${moment.coach}` : ''}
                  </span>
                </div>

                <button
                  type="button"
                  className="contact__knop"
                  onClick={() => setBewerktId(open ? null : moment.id)}
                  aria-label={open ? 'Klaar met bewerken' : 'Dit contactmoment aanpassen'}
                  aria-expanded={open}
                >
                  <PotloodIcoon />
                </button>
              </div>

              {open && (
                <div className="contact__inhoud">
                  {/* Tegels en geen uitklaplijst: zes vaste opties waar een
                      coach anders doorheen moet scrollen, met een telefoon in
                      één hand. */}
                  <TegelKiezer<ContactSoort>
                    label="Soort contact"
                    waarde={moment.soort}
                    opties={CONTACT_SOORTEN}
                    bijWijziging={(waarde) => zet(moment.id, 'soort', waarde)}
                    iconen={CONTACT_ICONEN}
                  />

                  <div className="veldenrij veldenrij--twee">
                    <Tekstveld
                      label="Datum"
                      type="date"
                      waarde={moment.datum}
                      bijWijziging={(waarde) => zet(moment.id, 'datum', waarde)}
                    />
                    <Tekstveld
                      label="Coach"
                      waarde={moment.coach}
                      bijWijziging={(waarde) => zet(moment.id, 'coach', waarde)}
                      autoComplete="name"
                    />
                  </div>

                  <TegelKiezer<UitkomstBezoek>
                    label="Uitkomst"
                    waarde={moment.uitkomst}
                    opties={UITKOMSTEN_BEZOEK}
                    bijWijziging={(waarde) => zet(moment.id, 'uitkomst', waarde)}
                  />

                  <Tekstvak
                    label="Wat is er besproken?"
                    lezers="team"
                    waarde={moment.toelichting}
                    bijWijziging={(waarde) => zet(moment.id, 'toelichting', waarde)}
                    placeholder="Korte beschrijving van dit contact."
                    rijen={3}
                    hint={<Schrijftip />}
                  />

                  <div className="contact__acties">
                    <Knop variant="secundair" compact onClick={() => setBewerktId(null)}>
                      Klaar
                    </Knop>
                    {/* Het eerste moment hoort bij het ontstaan van de
                        registratie; dat weghalen laat een adres achter zonder
                        enige aanleiding. */}
                    {!isEerste && (
                      <Knop
                        variant="gevaar"
                        compact
                        icoon={<PrullenbakIcoon />}
                        onClick={() => verwijder(moment.id)}
                      >
                        Verwijderen
                      </Knop>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <Knop variant="secundair" breed onClick={nieuwMoment}>
        Contactmoment toevoegen
      </Knop>

      <p className="veld__hint contact__uitleg">
        Voeg alleen iets toe als er echt contact is geweest. Een foto bijplakken of een typefout
        herstellen kan gewoon zonder.
      </p>
    </>
  );
}
